import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    useCreateZoneUserAccess,
    CreateZoneUserAccessFormSchema,
    type CreateZoneUserAccessFormSchemaValues,
} from 'src/features/parkingManagement/api/ZoneUserAccess/useCreateZoneUserAccess';
import { useDeleteZoneUserAccess } from 'src/features/parkingManagement/api/ZoneUserAccess/useDeleteZoneUserAccess';
import { useUpdateZoneUserAccess } from 'src/features/parkingManagement/api/ZoneUserAccess/useUpdateZoneUserAccess';
import ZoneUserAccessForm from 'src/features/parkingManagement/components/ZoneUserAccess/forms/ZoneUserAccessForm';
import { type ZoneUserAccess } from 'src/features/parkingManagement/types/zoneUserAccess';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

interface ZoneUserAccessFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    isDialogOpen?: boolean;
    selectedZoneUserAccess?: ZoneUserAccess;
    children?: React.ReactNode;
    isDelete?: boolean;
    removeTrigger?: boolean;
    onCloseDialog?: () => void;
}

export default function ZoneUserAccessFormModal<TData>({
    isDialogOpen = false,
    selectedZoneUserAccess,
    className,
    semantics,
    children,
    isDelete,
    removeTrigger,
    onSubmit: updateTable,
    onCloseDialog,
}: ZoneUserAccessFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingRole } = useParking();
    const { parkingId } = useParams();

    const { mutate: createZoneUserAccess, isPending: isPendingCreate } = useCreateZoneUserAccess({
        onSuccess: ({ zone_id }: ZoneUserAccess) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(generalMessages.successfulCreate),
                description: formatMessage(parkingManagementMessages.createZoneUserAccessSuccessfulDescription, {
                    zone_id,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: updateZoneUserAccess, isPending: isPendingUpdate } = useUpdateZoneUserAccess({
        onSuccess: ({ zone_id }: ZoneUserAccess) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(generalMessages.successfulEdit),
                description: formatMessage(parkingManagementMessages.updateZoneUserAccessSuccessfulDescription, {
                    zone_id,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: deleteZoneUserAccess, isPending: isPendingDelete } = useDeleteZoneUserAccess({
        onSuccess: () => {
            if (updateTable) updateTable();
            toast({
                title: formatMessage(generalMessages.successfulDelete),
                description: formatMessage(parkingManagementMessages.deleteZoneUserAccessSuccessfulDescription, {
                    zone_id,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const { zone_id = '', user_roles = [] } = selectedZoneUserAccess || {};

    const form = useForm<CreateZoneUserAccessFormSchemaValues>({
        resolver: zodResolver(CreateZoneUserAccessFormSchema),
        defaultValues: { zone_id, user_roles },
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(selectedZoneUserAccess);
    }, [selectedZoneUserAccess]);

    const onSubmit = (values: CreateZoneUserAccessFormSchemaValues) => {
        const submitData = {
            parkingId: parkingId || '',
            ...values,
        };
        if (!selectedZoneUserAccess?.id) return createZoneUserAccess(submitData);
        if (isDelete && parkingId)
            return deleteZoneUserAccess({ zoneUserAccessId: selectedZoneUserAccess.id, parkingId: parkingId });
        return updateZoneUserAccess({ zoneUserAccessId: selectedZoneUserAccess.id, ...submitData });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (setIsModalOpen) setIsModalOpen(isOpen);
        if (!isOpen) {
            form.reset();
            if (onCloseDialog) onCloseDialog();
        }
    };

    const getDialogTitle = () => {
        if (isDelete) return formatMessage(parkingManagementMessages.deleteZoneUserAccess);

        if (selectedZoneUserAccess?.id) return formatMessage(parkingManagementMessages.editZoneUserAccess);

        return formatMessage(tableMessages.new, {
            entry: semantics?.entryName?.toLowerCase(),
        });
    };

    const getDialogDescription = () => {
        if (isDelete) return formatMessage(parkingManagementMessages.deleteZoneUserAccess);

        if (selectedZoneUserAccess?.id) return formatMessage(parkingManagementMessages.editZoneUserAccess);

        return formatMessage(parkingManagementMessages.createZoneUserAccess);
    };

    const getDialogIcon = () => {
        if (isDelete) return ICON_MAP.DELETE;
        if (selectedZoneUserAccess?.id) return ICON_MAP.EDIT;
        return ICON_MAP.ADD;
    };

    if (parkingRole == MEMBERSHIP_ROLES.CLIENT) return <></>;
    return (
        <Dialog modal open={isModalOpen || isDialogOpen} onOpenChange={onOpenChange}>
            {!children && !selectedZoneUserAccess && !removeTrigger && (
                <DialogTrigger asChild>
                    <Button variant="outline" className={cn('ml-auto', className)}>
                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                        {formatMessage(tableMessages.new, {
                            entry: semantics?.entryName?.toLowerCase(),
                        })}
                    </Button>
                </DialogTrigger>
            )}
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader className={cn(isDelete && 'flex-row gap-4')}>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={getDialogIcon()} />
                        <div>
                            <DialogTitle>{getDialogTitle()}</DialogTitle>
                            <DialogDescription>{getDialogDescription()}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />
                <DialogBody>
                    {isDelete ? (
                        <p className="text-gray-500">
                            {formatMessage(parkingManagementMessages.confirmDeleteZoneUserAccess, {
                                zone_id: selectedZoneUserAccess?.zone_id,
                            })}
                        </p>
                    ) : (
                        <ZoneUserAccessForm form={form} onSubmit={onSubmit} />
                    )}
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        variant={isDelete ? 'destructiveLight' : 'default'}
                        disabled={isPendingCreate || isPendingUpdate || isPendingDelete}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPendingCreate || isPendingUpdate || isPendingDelete ? (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(
                            isDelete
                                ? parkingManagementMessages.deleteZoneUserAccess
                                : selectedZoneUserAccess?.id
                                  ? parkingManagementMessages.editZoneUserAccess
                                  : parkingManagementMessages.createZoneUserAccess,
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
